import { useMemo, useRef } from 'react';
import type { DeckDoc } from '../types/slide-doc';
import type { LessonPlayhead } from '../runtime/lessonPlayhead';

interface Props {
  deck: DeckDoc;
  playhead: LessonPlayhead;
  currentMs: number;
  durationMs: number;
  isPlaying: boolean;
  isPaused: boolean;
}

export function ScrubBar({ deck, playhead, currentMs, durationMs, isPlaying, isPaused }: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);

  const chapterMarkers = useMemo(() => {
    if (!deck.chapters || durationMs === 0) return [];
    const timings = playhead.getTimings();
    return deck.chapters.map((ch) => {
      const t = timings.find((tt) => tt.slide.id === ch.slideId);
      const pct = t ? (t.startMs / durationMs) * 100 : 0;
      return { id: ch.id, pct };
    });
  }, [deck, durationMs, playhead]);

  const pct = durationMs > 0 ? (currentMs / durationMs) * 100 : 0;

  const onSeek = (e: React.PointerEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    playhead.seek(ratio * durationMs);
  };

  return (
    <div style={containerStyle}>
      <button
        style={playBtnStyle}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        onClick={() => (isPlaying ? playhead.pause() : playhead.play())}
      >
        {isPlaying ? '❚❚' : '▶'}
      </button>
      <div style={timeStyle}>{formatTime(currentMs)}</div>
      <div ref={trackRef} style={trackStyle} onPointerDown={onSeek}>
        <div style={{ ...fillStyle, width: `${pct}%` }} />
        {chapterMarkers.map((m) => (
          <div key={m.id} style={{ ...chapterTickStyle, left: `${m.pct}%` }} />
        ))}
        <div style={{ ...headStyle, left: `${pct}%` }} />
      </div>
      <div style={timeStyle}>{formatTime(durationMs)}</div>
      {isPaused && !isPlaying && (
        <div style={{ ...badgeStyle, background: '#F5B800', color: '#0F1923' }}>PAUSED</div>
      )}
    </div>
  );
}

function formatTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  left: 24,
  right: 24,
  bottom: 24,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '12px 18px',
  background: 'rgba(15,25,35,0.78)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 999,
  backdropFilter: 'blur(10px)',
  zIndex: 30,
  fontFamily: 'Inter, sans-serif',
};

const playBtnStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 20,
  background: '#F5B800',
  color: '#0F1923',
  border: 'none',
  fontSize: 14,
  fontWeight: 800,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const trackStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  height: 8,
  background: 'rgba(255,255,255,0.10)',
  borderRadius: 4,
  cursor: 'pointer',
};

const fillStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to right, #F5B800, #FBC52E)',
  borderRadius: 4,
  pointerEvents: 'none',
};

const chapterTickStyle: React.CSSProperties = {
  position: 'absolute',
  top: -2,
  width: 2,
  height: 12,
  background: 'rgba(255,255,255,0.7)',
  borderRadius: 1,
  pointerEvents: 'none',
  transform: 'translateX(-1px)',
};

const headStyle: React.CSSProperties = {
  position: 'absolute',
  top: -4,
  width: 16,
  height: 16,
  borderRadius: 8,
  background: '#fff',
  border: '2px solid #F5B800',
  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  transform: 'translateX(-8px)',
  pointerEvents: 'none',
};

const timeStyle: React.CSSProperties = {
  color: '#E8E8E8',
  fontSize: 13,
  fontFamily: 'ui-monospace, monospace',
  minWidth: 42,
  textAlign: 'center',
};

const badgeStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 1.2,
};
